import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import type { ClientUser, ClientRole } from '../types';

interface UseClientAuthReturn {
  /** The authenticated client user, or null if not authenticated */
  user: ClientUser | null;
  /** Whether the authentication check is in progress */
  isLoading: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the user's account is enabled */
  isEnabled: boolean;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Refresh the authentication state */
  refresh: () => Promise<void>;
}

/**
 * useClientAuth - Client authentication state hook
 * 
 * Extracts user information from Cognito session tokens including:
 * - User ID, email, name
 * - Role (CLIENT or PROSPECT)
 * - Account enabled status
 * 
 * Requirements: 1.6, 1.8
 */
export function useClientAuth(): UseClientAuthReturn {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get current authenticated user
      const currentUser = await getCurrentUser();
      
      // Fetch session to get tokens with custom attributes
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        throw new Error('No ID token available');
      }

      // Extract user data from Cognito ID token payload (Requirement 1.8)
      const payload = idToken.payload;
      
      const role = payload['custom:role'] as ClientRole;
      const enabled = payload['custom:isEnabled'] as string;
      
      // Build client user object from token claims
      const clientUser: ClientUser = {
        id: currentUser.userId,
        email: payload.email as string || '',
        firstName: payload['custom:firstName'] as string || payload.given_name as string || '',
        lastName: payload['custom:lastName'] as string || payload.family_name as string || '',
        role: role || 'CLIENT',
        phoneNumber: payload['custom:phoneNumber'] as string || payload.phone_number as string,
        isEnabled: enabled !== 'false', // Default to true if not set
      };

      // Parse address if available
      const addressStr = payload['custom:address'] as string;
      if (addressStr) {
        try {
          clientUser.address = JSON.parse(addressStr);
        } catch {
          // Address parsing failed, leave undefined
        }
      }

      setUser(clientUser);
      setIsAuthenticated(true);
      setIsEnabled(clientUser.isEnabled);
    } catch {
      // Not authenticated or error occurred
      setUser(null);
      setIsAuthenticated(false);
      setIsEnabled(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsEnabled(true);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  const refresh = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    user,
    isLoading,
    isAuthenticated,
    isEnabled,
    signOut,
    refresh,
  };
}
